<template>
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="players"
      hide-default-footer
      :items-per-page="pagination.per_page"
      sort-by="last_name"
    >
      <!--<editor-fold desc="ITEM SLOT">-->
      <template
        v-slot:item="{ item: player }"
      >
        <tr>
          <td>
            <router-link :to="{ name: $routeNames.ADMIN_PLAYER_EDIT, params: { id: player.id }}">
              <div class="d-flex align-center">
                <v-avatar
                  size="32px"
                  class="mr-2"
                  color="grey lighten-2"
                >
                  <v-img :src="player.photo_url">
                    <template v-slot:placeholder>
                      <v-icon x-small>
                        fal fa-user
                      </v-icon>
                    </template>
                  </v-img>
                </v-avatar>
                {{ player.first_name }} {{ player.last_name }}
              </div>
            </router-link>
          </td>
          <td>
            <v-chip
              v-for="position in player.player_positions"
              :key="player.id + position.id"
              small
              color="grey darken-1"
              dark
            >
              {{ $t(`player_position.${toCamelCase(position.name)}`) }}
            </v-chip>
          </td>
          <td>
            {{ $dayjs(player.birth_date).format('L') }}
          </td>
          <td>
            <span v-if="player.team">
              {{ player.team.name }}
            </span>
            <span
              v-else
              class="grey--text"
            >
              N/A
            </span>
          </td>
          <td>
            <v-chip
              v-if="player.player_status"
              small
            >
              {{ $t(`player_status.${player.player_status.name}`) }}
            </v-chip>
            <span
              v-else
              class="grey--text"
            >
              N/A
            </span>
          </td>
          <td>
            <span v-if="player.user">
              {{ player.user.username }}
            </span>
            <span
              v-else
              class="grey--text"
            >
              N/A
            </span>
          </td>
          <td>
            <div v-if="player.user">
              <v-chip
                v-if="player.user.is_setup_complete"
                small
                color="success"
              >
                {{ $t('status.account_setup.complete') }}
              </v-chip>
              <v-chip
                v-else-if="player.user.is_invitation_expired"
                small
                color="error"
              >
                {{ $t('status.account_setup.expired') }}
                <v-icon
                  x-small
                  class="ml-2"
                >
                  far fa-triangle-exclamation
                </v-icon>
              </v-chip>
              <v-chip
                v-else
                small
              >
                {{ $t('status.account_setup.pending') }}
              </v-chip>
            </div>
            <span
              v-else
              class="grey--text"
            >
              N/A
            </span>
          </td>
          <td class="caption">
            <div v-if="player.user && player.user.last_mobile">
              <v-chip
                small
                label
                outlined
                class="text-truncate mr-2"
              >
                <div
                  style="max-width: 150px"
                  class="text-truncate"
                >
                  {{ player.user.last_mobile.name }}
                </div>
              </v-chip>
              <span>
                {{ $dayjs(player.user.last_mobile.last_used_at).fromNow() }}
              </span>
            </div>
            <div v-else>
              {{ $t('ultrax_mobile_used.not_used') }}
            </div>
          </td>
          <td v-if="user && user.organisation_settings && user.organisation_settings.has_membership_module">
            <span v-if="player.membership_plan">
              {{ player.membership_plan.title }}
            </span>
            <span
              v-else
              class="grey--text"
            >
              N/A
            </span>
          </td>
          <td style="width: 0;">
            <v-btn
              icon
              :to="{ name: $routeNames.ADMIN_PLAYER_EDIT, params: { id: player.id }}"
            >
              <v-icon small>
                fal fa-edit
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <!--</editor-fold>-->
      <!--<editor-fold desc="PROGRESS SLOT">-->
      <template
        v-if="loading"
        v-slot:body
      >
        <tbody>
          <tr
            v-for="i in pagination.per_page"
            :key="i"
          >
            <td class="d-flex align-center">
              <div class="d-inline-flex align-center">
                <v-skeleton-loader
                  type="avatar"
                  class="mr-3"
                />
                <v-skeleton-loader
                  type="text"
                  width="100px"
                />
              </div>
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td>
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td v-if="user && user.organisation_settings && user.organisation_settings.has_membership_module">
              <v-skeleton-loader
                type="text"
                max-width="60px"
              />
            </td>
            <td />
          </tr>
        </tbody>
      </template>
      <!--</editor-fold>-->
    </v-data-table>
    <div
      v-if="pagination && pagination.last_page > 1"
      class="d-flex justify-center align-center py-5"
    >
      <pagination
        v-model="pagination.current_page"
        :last-page="pagination.last_page"
      />
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination.vue'
import i18n from '@/plugins/i18n'
import { toCamelCase } from '../../utils/helpers'

export default {
  name: 'AdminPlayersListTable',
  components: { Pagination },
  props: {
    players: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    editPlayer: {
      player: undefined,
      visible: false
    }
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    tableHeaders() {
      return [
        { value: 'name', text: i18n.t('table_headers.name'), sortable: true },
        { value: 'player_positions', text: i18n.t('table_headers.player_overview.position'), sortable: true },
        { value: 'birth_date', text: i18n.t('text_input.data.date_of_birth.title'), sortable: true },
        { value: 'team', text: i18n.t('dropdown.team.title'), sortable: true },
        { value: 'player_status.name', text: i18n.t('table_headers.player_overview.status'), sortable: true },
        { value: 'user.username', text: i18n.t('text_input.user.username.title'), sortable: true },
        { value: 'user.is_setup_complete', text: i18n.t('table_headers.admin_all_players.account_setup'), sortable: true },
        { value: 'user.last_mobile', text: i18n.t('table_headers.admin_all_players.ultrax_mobile_used'), sortable: true },
        ...(this.user && this.user.organisation_settings && this.user.organisation_settings.has_membership_module ? [{ value: 'membership_plan_id', text: this.$t('new_player.membership_plan.title'), sortable: true }] : []),
        { sortable: false }
      ]
    }
  },
  methods: {
    toCamelCase,
    onTableRowClick(playerId) {
      this.$emit('tableRowClick', playerId)
    }
  }
}
</script>
